/**
 * InstallationConfirmationModal Component
 *
 * Purpose: Confirmation dialog before executing deterministic installation.
 * Shows final summary and requires explicit user confirmation.
 *
 * Design: Glassmorphism modal with clear warning indicators
 */

import type React from "react";

interface InstallationConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  configuration: {
    estimatedDuration: number;
    estimatedCost: number;
    modules: Array<{ name: string }>;
  };
}

export const InstallationConfirmationModal: React.FC<InstallationConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  configuration,
}) => {
  if (!isOpen) return null;

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Warning Header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Execute Installation?</h2>
          <p className="text-gray-600">
            This will trigger a deterministic installation process on your infrastructure.
          </p>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Modules to install:</span>
            <span className="font-semibold text-gray-900">{configuration.modules.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Estimated duration:</span>
            <span className="font-semibold text-gray-900">
              {formatDuration(configuration.estimatedDuration)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Monthly cost:</span>
            <span className="font-semibold text-green-600">
              ${configuration.estimatedCost.toFixed(2)}/mo
            </span>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="text-xl mr-3">💡</div>
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">This action will:</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Provision infrastructure resources</li>
                <li>Install and configure {configuration.modules.length} Odoo modules</li>
                <li>Begin incurring infrastructure costs</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-lg"
          >
            Execute Installation
          </button>
        </div>
      </div>
    </div>
  );
};
